/**
 * Construction Drive-link matching helpers.
 *
 * GHL opportunities ("Client - Address") are matched to Monday construction
 * items ("Client | Address") by ADDRESS, never by client name/email alone —
 * one client can have many projects and name/email matching collides.
 *
 * Ambiguous matches resolve to '' — a missing link is better than a wrong one.
 */

// Common US street-suffix abbreviations → canonical form
const STREET_ABBREV = {
    st: 'street', str: 'street',
    ave: 'avenue', av: 'avenue', aven: 'avenue',
    rd: 'road',
    dr: 'drive', drv: 'drive',
    ct: 'court',
    ln: 'lane',
    blvd: 'boulevard',
    ter: 'terrace', trce: 'terrace', terr: 'terrace',
    pkwy: 'parkway', pky: 'parkway',
    hwy: 'highway',
    pl: 'place',
    cir: 'circle',
    wy: 'way',
    sq: 'square',
}

/**
 * Normalize an address string for comparison:
 * lowercase, strip punctuation/parentheticals, collapse ordinals ("35th"→"35"),
 * expand street-suffix abbreviations, collapse whitespace.
 */
export function normalizeAddress(s) {
    const t = (s || '').toLowerCase()
        .replace(/\([^)]*\)/g, ' ')          // drop "(NA-01)" and other parentheticals
        .replace(/[^a-z0-9]+/g, ' ')          // punctuation → space
        .trim()
    if (!t) return ''
    return t.split(/\s+/)
        .map(w => {
            const ord = w.match(/^(\d+)(st|nd|rd|th)$/)
            if (ord) return ord[1]
            return STREET_ABBREV[w] || w
        })
        .join(' ')
}

/** Extract the address segment from a Monday item name "Client | Address". */
export function mondayAddressFromName(name) {
    const parts = (name || '').split('|')
    return normalizeAddress(parts[parts.length - 1])
}

/** Extract the address segment from a GHL opportunity name "Client - Address". */
export function ghlAddressFromName(name) {
    const n = (name || '')
    const i = n.indexOf(' - ')
    return normalizeAddress(i >= 0 ? n.slice(i + 3) : n)
}

/**
 * True when one normalized address is a token-prefix of the other
 * (handles trailing unit/city/state differences on either side).
 * Diverging tokens (e.g. "6120 sw" vs "6120 nw") → no match.
 */
function addressMatch(a, b) {
    if (!a || !b) return false
    if (a === b) return true
    const ta = a.split(' '), tb = b.split(' ')
    const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta]
    return short.length >= 3 && short.every((tok, i) => tok === long[i])
}

/**
 * Build lookup indexes from raw Monday construction items.
 * rawItems: [{ item }] where item has .name and .column_values.
 * emailColId / nameColId: client email / client name text columns.
 * Returns { byAddress: [{ addr, url }], clientUrls: Map<key, Set<url>> }
 */
export function buildConLinkIndex(rawItems, { emailColId, nameColId } = {}) {
    const byAddress = []
    const clientUrls = new Map()
    for (const { item } of rawItems || []) {
        const cols = item.column_values || []
        let url = ''
        for (const col of cols) {
            if (col.type !== 'link') continue
            try {
                const parsed = JSON.parse(col.value || '{}')
                if (parsed?.url) { url = parsed.url; break }
            } catch { /* ignore unparsable link values */ }
        }
        if (!url) continue

        const addr = mondayAddressFromName(item.name)
        if (addr) byAddress.push({ addr, url })

        const keys = [
            (cols.find(c => c.id === emailColId)?.text || '').toLowerCase().trim(),
            (cols.find(c => c.id === nameColId)?.text || '').toLowerCase().trim(),
        ]
        for (const k of keys) {
            if (!k) continue
            if (!clientUrls.has(k)) clientUrls.set(k, new Set())
            clientUrls.get(k).add(url)
        }
    }
    return { byAddress, clientUrls }
}

/**
 * Resolve a GHL construction project's Drive link.
 * Priority:
 *   1. Address match — all matching items must point to the same URL.
 *   2. Client fallback (email or name) — only when it yields exactly ONE url.
 * Anything ambiguous → ''.
 */
export function resolveConDriveLink(project, index) {
    const addr = ghlAddressFromName(project?.name)
    if (addr) {
        const urls = new Set(
            index.byAddress
                .filter(e => addressMatch(addr, e.addr))
                .map(e => e.url)
        )
        if (urls.size === 1) return [...urls][0]
        if (urls.size > 1) return ''
    }

    // Client-level fallback — safe only when it resolves to a single url
    const urls = new Set()
    for (const k of [
        (project?.clientEmail || '').toLowerCase().trim(),
        (project?.clientName || '').toLowerCase().trim(),
    ]) {
        for (const u of index.clientUrls.get(k) || []) urls.add(u)
    }
    return urls.size === 1 ? [...urls][0] : ''
}
